<template>
  <!--
    InsNodeRow — the shared, purely presentational row for the insurance tree.
    Used by BOTH the Miller-column cells and the breadcrumb-drill list. It is
    layout-neutral: a single horizontal row at the parent's width; the parent
    owns indentation. Driven entirely by props — no API, no Vuex, no element-ui.

    Click contract:
      - clicking the row BODY        → emits `select` (payload: node)
      - clicking the chevron         → emits `toggle` (payload: node) and
                                       STOPS propagation, so expanding/collapsing
                                       a branch never changes the current
                                       selection. (Rationale: in a Miller column
                                       you often want to peek into a branch's
                                       children without losing the node you were
                                       inspecting; select-on-toggle would fight
                                       that. Selecting is an explicit body click.)
      - keyboard: row is focusable; Enter / Space → select.
  -->
  <div
    class="ins-row"
    :class="{
      'is-selected': selected,
      'is-inactive': isInactive,
      'is-leaf': !hasChildren,
    }"
    role="treeitem"
    :tabindex="0"
    :aria-selected="selected ? 'true' : 'false'"
    :aria-expanded="hasChildren ? String(expanded) : null"
    :title="node.Name"
    @click="onSelect"
    @keydown.enter.prevent="onSelect"
    @keydown.space.prevent="onSelect"
  >
    <!-- optional outline-number prefix (future "show outline numbers" toggle) -->
    <span
      v-if="outlineNumber"
      class="ins-row__outline c-mono"
      aria-hidden="true"
    >{{ outlineNumber }}</span>

    <!-- expand/collapse affordance, OR a leaf marker — fixed width so the
         Name column stays aligned whether the node has children or not. -->
    <button
      v-if="hasChildren"
      type="button"
      class="ins-row__chev"
      :class="{ 'is-open': expanded }"
      :aria-label="expanded ? 'collapse' : 'expand'"
      @click="onToggle"
    >&#9656;</button>
    <span v-else class="ins-row__leaf" aria-hidden="true">&middot;</span>

    <!-- the node Name — NEVER ShowName. Single-line, ellipsis-truncated;
         the FULL real name is always exposed via the row `title` (above) for
         hover/tooltip, so the hidden outline prefix stays discoverable. -->
    <span class="ins-row__name">{{ displayName(node.Name, stripPrefix) }}</span>

    <!-- dataType chip: colour accent + the translated label text (not colour
         alone). DISPLAY-ONLY: hidden when `hideTypeChip` so the (1 1 auto) name
         reclaims the freed width — the chip is `flex: 0 0 auto`, so removing it
         hands its space straight to the name with no CSS change needed. -->
    <span
      v-if="!hideTypeChip"
      class="ins-row__chip"
      :style="{ '--chip-color': meta.color }"
    >
      <span class="ins-row__chip-dot" aria-hidden="true"></span>
      <span class="ins-row__chip-label">{{ $t(meta.key) }}</span>
    </span>

    <!-- active / inactive indicator. Active = filled dot (quiet). Inactive =
         hollow ring + an explicit "ปิดใช้งาน" tag (text, not colour-only);
         the whole row is also dimmed via the .is-inactive class. -->
    <span class="ins-row__state">
      <span
        class="ins-row__state-dot"
        :class="isInactive ? 'is-off' : 'is-on'"
        aria-hidden="true"
      >{{ isInactive ? '○' : '●' }}</span>
      <span v-if="isInactive" class="ins-row__state-tag">{{
        $t("lmg.insure_inactive_tag")
      }}</span>
    </span>
  </div>
</template>

<script>
import { dataTypeMeta } from "./insDataType";
import { displayName } from "./insName";

export default {
  name: "ins-node-row",
  props: {
    node: { type: Object, required: true },
    hasChildren: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false },
    outlineNumber: { type: String, default: "" },
    // DISPLAY-ONLY: hide the leading outline number from the shown name.
    stripPrefix: { type: Boolean, default: false },
    // DISPLAY-ONLY: hide the per-row dataType chip (it's inherited from the
    // subtree root, so it's redundant and eats name width in narrow columns).
    hideTypeChip: { type: Boolean, default: false },
  },
  computed: {
    meta() {
      return dataTypeMeta(this.node.DataType);
    },
    isInactive() {
      // IsActive may arrive as number 0/1 or string "0"/"1".
      return Number(this.node.IsActive) === 0;
    },
  },
  methods: {
    displayName,
    onSelect() {
      this.$emit("select", this.node);
    },
    onToggle(e) {
      // stop the body click so toggling does not also select (see template note)
      e.stopPropagation();
      this.$emit("toggle", this.node);
    },
  },
};
</script>

<style scoped>
.ins-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px; /* iPad touch target */
  padding: 4px 12px;
  box-sizing: border-box;
  border-left: 3px solid transparent; /* reserved for selected accent */
  cursor: pointer;
  color: var(--c-text, #2c3e50);
  font-family: var(--c-font, inherit);
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease);
  outline: none;
}
.ins-row:hover {
  background: var(--c-tint, #eef3fa);
}
.ins-row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--c-blue, #3b6ea8);
}
.ins-row.is-selected {
  background: var(--c-blue-soft, #ebf2fa);
  border-left-color: var(--c-blue, #3b6ea8);
}
.ins-row.is-inactive {
  opacity: 0.55;
}

/* outline number prefix */
.ins-row__outline {
  flex: 0 0 auto;
  min-width: 2.5em;
  color: var(--c-text-mute, #8a96a8);
  font-size: 12px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* chevron / leaf marker — fixed footprint keeps the Name aligned */
.ins-row__chev,
.ins-row__leaf {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
}
.ins-row__chev {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--c-text-soft, #5e6c80);
  cursor: pointer;
  border-radius: 4px;
  transition: transform var(--c-fast, 140ms) var(--c-ease, ease),
    background-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.ins-row__chev:hover {
  background: var(--c-neutral-soft, #eef0f4);
}
.ins-row__chev.is-open {
  transform: rotate(90deg);
}
.ins-row__leaf {
  color: var(--c-text-mute, #8a96a8);
  font-size: 14px;
}

/* name — single line, ellipsis (Thai has no word spaces; never wrap) */
.ins-row__name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}

/* dataType chip */
.ins-row__chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 40%;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--c-surface-soft, #fafbfd);
  border: 1px solid var(--c-border, #e4e9f1);
  font-size: 12px;
  color: var(--c-text-soft, #5e6c80);
  white-space: nowrap;
}
.ins-row__chip-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--chip-color, #8a96a8);
}
.ins-row__chip-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* active / inactive state */
.ins-row__state {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ins-row__state-dot {
  font-size: 10px;
  line-height: 1;
}
.ins-row__state-dot.is-on {
  color: var(--c-success, #38866b);
}
.ins-row__state-dot.is-off {
  color: var(--c-text-mute, #8a96a8);
}
.ins-row__state-tag {
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--c-neutral-soft, #eef0f4);
  color: var(--c-text-soft, #5e6c80);
  font-size: 11px;
  white-space: nowrap;
}
</style>
